import { RsHTMLElement } from '../rs-htmlelement.js';
import { buildSort, parseCriterionExpression } from '@coveo/headless';
import styles from 'inline:./rs-sort-dropdown.scss';

export class RsSortDropdown extends RsHTMLElement {
    sort;
    sortExpressions = [];
    selectedSortOption;

    styles() {
        return styles;
    }

    async connected() {
        if (this.autofill) {
            this.sortExpressions = this.bindings.store.state.sortOptions;
        } else {
            await this.buildSortExpressions();
        }
    }

    async mounted() {
        this.initEvents();

        this.sort = buildSort(this.bindings.engine);
        this.sort.subscribe(() => {
            this.selectSort(this.sort.state.sortCriteria);
        });
    }

    async buildSortExpressions() {
        await customElements.whenDefined('rs-sort-expression');

        this.sortExpressions = Array.from(this.querySelectorAll(':scope > rs-sort-expression')).map((sortExpression) => ({
            criteria: parseCriterionExpression(sortExpression.getAttribute('expression')),
            expression: sortExpression.getAttribute('expression'),
            label: sortExpression.getAttribute('label'),
            tabs: {
                included: [],
                excluded: []
            }
        }));

        this.bindings.store.state.sortOptions = this.sortExpressions;
    }

    closeDropdown() {
        this.selectedWrapper.classList.remove('open');
        this.options.classList.remove('visible');
    }

    initEvents() {
        // close dropdown if clicked outside
        this.root.addEventListener('blur', () => {
            this.closeDropdown();
        })

        // toggle dropdown if clicked
        this.root.addEventListener('click', (event) => {
            if (event.target.classList.contains('option')) {
                return;
            }

            this.selectedWrapper.classList.toggle('open');
            this.options.classList.toggle('visible');
        });

        // set sort expression and close dropdown if option is clicked
        this.optionsList.forEach((option) => {
            option.addEventListener('click', () => {
                if (this.selectedSortOption.label !== option.dataset.value) {
                    const sortOption = this.sortExpressions.find((sortOption) => sortOption.expression === option.dataset.value);
                    this.sort.sortBy(sortOption.criteria);
                }

                this.closeDropdown();
            });
        });
    }

    selectSort(criteria) {
        this.selectedSortOption = this.sortExpressions.find((sortOption) => sortOption.expression === criteria.replaceAll('@', ''));

        if (this.selectedSortOption) {
            this.selectedLabel.textContent = this.selectedSortOption.label;
        }
    }

    get autofill() {
        return this.hasAttribute('autofill') && this.getAttribute('autofill') !== 'false';
    }

    get classes() {
        const classes = ['sort-dropdown'];

        if (this.isStandalone) {
            classes.push('standalone');
        }

        return classes.join(' ');
    }

    get hideOnMobile() {
        return this.hasAttribute('hide-on-mobile');
    }

    get isStandalone() {
        return this.hasAttribute('standalone');
    }

    get label() {
        return this.i18n.t('rs.sortDropdownSortBy');
    }

    get root() {
        return this.shadow.querySelector('.sort-dropdown');
    }

    get selected() {
        return this.shadow.querySelector('.selected');
    }

    get selectedWrapper() {
        return this.shadow.querySelector('.selected-wrapper');
    }

    get selectedLabel() {
        return this.shadow.querySelector('.selected-label');
    }

    get options() {
        return this.shadow.querySelector('.options');
    }

    get optionsList() {
        return Array.from(this.shadow.querySelectorAll('.option'));
    }

    renderChevron() {
        return `
            <svg viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path id="Icon" d="M0.85791 1.46448L7.92898 8.53555L11.4645 5.00001L15 1.46448" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
    }

    renderLabel() {
        if (this.label && !this.isStandalone) {
            return `
                <span class="label">${this.label}</span>
            `;
        }

        return '';
    }

    renderSelect() {
        return `
            <div class="selected-wrapper">
                <div class="selected" part="select">
                    <span class="selected-label">${this.selectedSortOption?.label}</span>
                    <div class="chevron">${this.renderChevron()}</div>
                </div>
            </div>
            <div class="options">
                ${this.sortExpressions.reduce((output, option) => `${output}<div class="option" data-value="${option.expression}">${option.label}</div>`, '')}
            </div>
        `;
    }

    render() {
        this.shadow.innerHTML = `
            <div class="${this.classes}" tabindex="0">
                ${this.renderLabel()}
                ${this.renderSelect()}
            </div>
        `;
    }
};